import React, { useEffect, useState } from "react";
import { X, Download, Share } from "lucide-react";

const DISMISS_KEY = "crece_pwa_dismissed_at";
const DISMISS_DAYS = 14;

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

export const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (Date.now() - dismissedAt < DISMISS_DAYS * 86400000) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setTimeout(() => setShow(true), 3000);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    let iosTimer;
    if (isIos()) {
      iosTimer = setTimeout(() => { setIos(true); setShow(true); }, 4000);
    }
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); clearTimeout(iosTimer); };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setShow(false);
    else dismiss();
    setDeferred(null);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-sm z-[9999]" data-testid="pwa-install-banner">
      <div className="bg-emerald-950 text-white rounded-2xl shadow-2xl border border-emerald-800 p-4 flex items-start gap-3">
        <img src="/icon-192.png" alt="Crece" className="w-11 h-11 rounded-xl flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm">Install the Crece app</div>
          {ios ? (
            <div className="text-xs text-emerald-200 mt-1 leading-relaxed">
              Tap <Share className="w-3.5 h-3.5 inline -mt-0.5" /> <b>Share</b> in Safari, then <b>"Add to Home Screen"</b> to install.
            </div>
          ) : (
            <div className="text-xs text-emerald-200 mt-1">Get the full-screen app experience — faster access to leads, products & earnings.</div>
          )}
          {!ios && (
            <button onClick={install} data-testid="pwa-install-button"
              className="mt-2.5 inline-flex items-center gap-1.5 bg-amber-400 hover:bg-amber-300 text-emerald-950 font-bold text-xs rounded-full px-4 py-2 transition-colors">
              <Download className="w-3.5 h-3.5" /> Install app
            </button>
          )}
        </div>
        <button onClick={dismiss} data-testid="pwa-dismiss-button" className="text-emerald-300 hover:text-white flex-shrink-0 p-0.5">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
